import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import GithubSlugger from 'github-slugger';
import { ArrowLeft, ArrowRight, MagnifyingGlass, WarningCircle } from '@phosphor-icons/react';
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const API = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_EMAIL = 'support@paydayrelay.com';

const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const MUTED = '#8B7E6A';
const GOLD = '#F5CB5C';
const CREAM = '#FDFBF7';
const PAPER = '#FAF1D8';

const SUBHEAD = 'Resources for understanding unstructured income, tax set-asides, weekly payouts, '
  + 'and building a rhythm around independent work.';

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const MD_COMPONENTS = {
  h1: ({ id, children }) => (
    <h2 id={id} className="text-2xl sm:text-3xl leading-snug mt-10 first:mt-0 mb-4 scroll-mt-24" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>{children}</h2>
  ),
  h2: ({ id, children }) => (
    <h3 id={id} className="text-xl sm:text-2xl leading-snug mt-9 mb-3 scroll-mt-24" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>{children}</h3>
  ),
  h3: ({ id, children }) => (
    <h4 id={id} className="text-lg leading-snug mt-7 mb-2 font-bold scroll-mt-24" style={{ color: INK }}>{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-base sm:text-lg leading-relaxed mb-5" style={{ color: INK2 }}>{children}</p>
  ),
  ul: ({ children }) => <ul className="mb-5 space-y-2">{children}</ul>,
  ol: ({ children }) => <ol className="mb-5 space-y-2 list-decimal pl-5">{children}</ol>,
  li: ({ children }) => (
    <li className="text-base leading-relaxed marker:text-[#B8860B]" style={{ color: INK2 }}>{children}</li>
  ),
  strong: ({ children }) => <strong style={{ color: INK }}>{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} className="underline" style={{ color: INK }} target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{children}</a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="pl-5 my-6" style={{ borderLeft: `3px solid ${GOLD}`, color: INK2 }}>{children}</blockquote>
  ),
  code: ({ children }) => (
    <code className="px-1.5 py-0.5 rounded text-sm" style={{ background: PAPER, color: INK }}>{children}</code>
  ),
  hr: () => <hr className="my-8" style={{ borderColor: 'rgba(42,30,92,0.12)' }} />,
};

const Shell = ({ children }) => (
  <div className="min-h-screen flex flex-col" style={{ background: CREAM }} data-testid="learn-page">
    <SiteNav />
    {children}
    <SiteFooter />
  </div>
);

const SupportLine = ({ onDark = false }) => (
  <p className="text-sm" style={{ color: onDark ? 'rgba(255,255,255,0.72)' : INK2 }}>
    Can&rsquo;t find it?{' '}
    <a
      href={`mailto:${SUPPORT_EMAIL}`}
      className="underline font-semibold"
      style={{ color: onDark ? GOLD : INK }}
      data-testid="learn-support-link"
    >
      Email Support
    </a>
  </p>
);

// Auto-build a numbered table of contents from the article markdown.
// Uses github-slugger in document order so ids match rehype-slug exactly.
const buildToc = (body) => {
  if (!body) return [];
  const slugger = new GithubSlugger();
  const out = [];
  let inCode = false;
  for (const line of body.split('\n')) {
    const t = line.trim();
    if (t.startsWith('```')) { inCode = !inCode; continue; }
    if (inCode) continue;
    const m = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].trim();
    const id = slugger.slug(text);   // slug every heading to keep dedupe aligned with rehype-slug
    if (level === 2 || level === 3) out.push({ level, text, id });
  }
  return out;
};

const GuideToc = ({ toc, activeId, onNavigate }) => (
  <nav aria-label="On this page">
    <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: MUTED }}>
      On this page
    </p>
    <ul className="space-y-1.5">
      {toc.map((h) => {
        const active = activeId === h.id;
        return (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              onClick={(e) => onNavigate(e, h.id)}
              className="block text-[13px] leading-snug py-0.5 transition-colors hover:opacity-80"
              style={{
                color: active ? INK : INK2,
                fontWeight: active ? 700 : 400,
                paddingLeft: h.level === 3 ? 18 : 10,
                borderLeft: `2px solid ${active ? GOLD : 'transparent'}`,
                marginLeft: -2,
              }}
              data-testid={`learn-toc-${h.id}`}
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ul>
  </nav>
);

export default function LearnPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, failed: false });
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/learn/articles`);
        if (!res.ok) { setState({ loading: false, data: null, failed: true }); return; }
        const data = await res.json();
        setState({ loading: false, data, failed: !!data.error });
      } catch {
        setState({ loading: false, data: null, failed: true });
      }
    })();
  }, []);

  const articles = useMemo(() => state.data?.articles || [], [state.data]);
  const categories = state.data?.categories || [];
  const wanted = slug ? String(slug).toLowerCase() : '';
  const category = categories.find((c) => (c.path || c.key) === wanted) || null;
  const article = wanted && !category ? articles.find((a) => a.slug === wanted) : null;

  const location = useLocation();
  const [activeId, setActiveId] = useState('');
  const toc = useMemo(() => buildToc(article?.body), [article]);
  const hasToc = toc.length >= 2;

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  // Scroll-spy: highlight the section currently in view.
  useEffect(() => {
    if (!article || !hasToc) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((en) => en.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActiveId(vis[0].target.id);
      },
      { rootMargin: '-84px 0px -68% 0px', threshold: 0 },
    );
    toc.forEach((h) => { const el = document.getElementById(h.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [article, hasToc, toc]);

  // Jump to a section if the URL already carries a hash.
  useEffect(() => {
    if (!article || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [article, location.hash]);

  useEffect(() => {
    if (state.loading) return;
    const title = article ? article.title : category ? category.name : 'Help Guides';
    document.title = `${title} · Payday Relay`;
    setMeta('description', article?.seo_description || category?.description || SUBHEAD);
  }, [article, category, state.loading]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return articles.filter((a) =>
      a.title.toLowerCase().includes(q)
      || (a.seo_description || '').toLowerCase().includes(q)
      || (a.category || '').toLowerCase().includes(q)
      || a.body.toLowerCase().includes(q));
  }, [query, articles]);

  /* ---------- one category ---------- */
  if (wanted && (category || state.loading)) {
    const items = category ? articles.filter((a) => a.category_key === category.key) : [];
    return (
      <Shell>
        <section className="px-5 sm:px-8 pt-12 sm:pt-16 pb-9 sm:pb-12" style={{ background: INK }}>
          <div className="max-w-3xl mx-auto">
            <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm font-semibold mb-7 hover:opacity-70" style={{ color: GOLD }} data-testid="learn-category-back">
              <ArrowLeft className="w-4 h-4" /> All help guides
            </Link>
            {category && (
              <>
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: GOLD }}>Learn</p>
                <h1 className="text-3xl sm:text-4xl leading-tight mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }} data-testid="learn-category-title">
                  {category.name}
                </h1>
                <p className="text-base leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.78)' }} data-testid="learn-category-description">
                  {category.description}
                </p>
              </>
            )}
          </div>
        </section>
        <main className="flex-1 px-5 sm:px-8 py-12 sm:py-16">
          <div className="max-w-3xl mx-auto">
            {state.loading ? (
              <p className="text-base" style={{ color: INK2 }} data-testid="learn-loading">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-base mb-8" style={{ color: '#8B7E6A' }} data-testid="learn-category-empty">
                Guides for this topic are on the way.
              </p>
            ) : (
              <div className="space-y-3 mb-10" data-testid="learn-category-list">
                {items.map((a) => <GuideRow key={a.slug} a={a} />)}
              </div>
            )}
            <SupportLine />
          </div>
        </main>
      </Shell>
    );
  }

  /* ---------- single guide ---------- */
  if (wanted) {
    return (
      <Shell>
        <main className="flex-1 px-5 sm:px-8 py-14 sm:py-20">
          {article ? (
            <div className={hasToc ? 'max-w-5xl mx-auto lg:grid lg:grid-cols-[240px_1fr] lg:gap-12' : 'max-w-2xl mx-auto'}>
              {hasToc && (
                <aside className="hidden lg:block" data-testid="learn-toc-desktop">
                  <div className="sticky top-8">
                    <GuideToc toc={toc} activeId={activeId} onNavigate={handleTocClick} />
                  </div>
                </aside>
              )}
              <article className="max-w-2xl" data-testid="learn-article">
                <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm font-semibold mb-9 hover:opacity-70" style={{ color: INK }} data-testid="learn-back-link">
                  <ArrowLeft className="w-4 h-4" /> All help guides
                </Link>
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: '#B8860B' }} data-testid="learn-article-category">
                  {article.category}
                </p>
                <h1 className="text-3xl sm:text-4xl leading-tight mb-8" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }} data-testid="learn-article-title">
                  {article.title}
                </h1>
                {article.hero_image && (
                  <img
                    src={article.hero_image}
                    alt={article.title}
                    loading="lazy"
                    className="w-full rounded-2xl mb-9 object-cover"
                    style={{ maxHeight: '420px', boxShadow: '0 6px 24px rgba(42,30,92,0.10)' }}
                    data-testid="learn-article-hero-image"
                  />
                )}
                {hasToc && (
                  <div className="lg:hidden rounded-2xl p-4 mb-9 border" style={{ background: '#FFFFFF', borderColor: '#E8E2D5' }} data-testid="learn-toc-mobile">
                    <GuideToc toc={toc} activeId={activeId} onNavigate={handleTocClick} />
                  </div>
                )}
                <div data-testid="learn-article-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]} components={MD_COMPONENTS}>
                    {article.body}
                  </ReactMarkdown>
                </div>
                <div className="mt-12 pt-7" style={{ borderTop: '1px solid rgba(42,30,92,0.12)' }}>
                  <p className="text-sm" style={{ color: INK2 }}>
                    Still stuck?{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-semibold" style={{ color: INK }} data-testid="learn-article-support-link">
                      Email Support
                    </a>
                  </p>
                </div>
              </article>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm font-semibold mb-9 hover:opacity-70" style={{ color: INK }} data-testid="learn-back-link">
                <ArrowLeft className="w-4 h-4" /> All help guides
              </Link>
              <div data-testid="learn-not-found">
                <h1 className="text-3xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                  That guide isn&rsquo;t available.
                </h1>
                <p className="text-base mb-6" style={{ color: INK2 }}>
                  It may not be published yet. Everything that is live is on the main help page.
                </p>
                <Link to="/learn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm" style={{ background: GOLD, color: INK }}>
                  Browse help guides <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </main>
      </Shell>
    );
  }

  /* ---------- hub ---------- */
  return (
    <Shell>
      <section className="px-5 sm:px-8 pt-14 sm:pt-20 pb-10 sm:pb-14" style={{ background: INK }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: GOLD }} data-testid="learn-eyebrow">Learn</p>
          <h1 className="text-4xl sm:text-5xl leading-[1.08] mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }} data-testid="learn-headline">
            Help Guides
          </h1>
          <p className="text-base md:text-lg leading-relaxed max-w-2xl mb-8" style={{ color: 'rgba(255,255,255,0.78)' }} data-testid="learn-subhead">
            {SUBHEAD}
          </p>

          <div className="relative max-w-xl">
            <MagnifyingGlass className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2" style={{ color: '#8B7E6A' }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search topics, e.g. quarterly taxes"
              aria-label="Search help guides"
              className="w-full pl-12 pr-5 py-4 rounded-full text-base focus:outline-none"
              style={{ background: CREAM, color: INK, border: '1px solid rgba(245,203,92,0.35)' }}
              data-testid="learn-search-input"
            />
          </div>

          <div className="mt-4">
            <SupportLine onDark />
          </div>
        </div>
      </section>

      <main className="flex-1 px-5 sm:px-8 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto">
          {state.loading && <p className="text-base" style={{ color: INK2 }} data-testid="learn-loading">Loading the guides…</p>}

          {!state.loading && state.failed && (
            <div className="rounded-2xl px-6 py-8 flex items-start gap-4 mb-10" style={{ background: PAPER }} data-testid="learn-error">
              <WarningCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#B8860B' }} />
              <div>
                <p className="text-base font-bold mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                  The guides are taking a moment.
                </p>
                <p className="text-sm leading-relaxed" style={{ color: INK2 }}>
                  We couldn&rsquo;t load the library just now. Try again shortly, or{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-semibold" style={{ color: INK }}>email support</a> and we&rsquo;ll help directly.
                </p>
              </div>
            </div>
          )}

          {/* Search results take over the page while typing */}
          {!state.loading && query.trim() && (
            <div className="mb-12" data-testid="learn-search-results">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-5" style={{ color: '#B8860B' }}>
                {results.length} result{results.length === 1 ? '' : 's'} for &ldquo;{query.trim()}&rdquo;
              </p>
              {results.length === 0 ? (
                <p className="text-base leading-relaxed" style={{ color: INK2 }} data-testid="learn-search-empty">
                  Nothing matched that yet.{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-semibold" style={{ color: INK }}>Ask us directly</a> and we&rsquo;ll write the guide.
                </p>
              ) : (
                <div className="space-y-3">
                  {results.map((a) => <GuideRow key={a.slug} a={a} />)}
                </div>
              )}
            </div>
          )}

          {/* Category hub */}
          {!state.loading && !query.trim() && (
            <div className="grid gap-5 sm:grid-cols-2" data-testid="learn-categories">
              {categories.map((c) => (
                <Link
                  key={c.key}
                  to={`/learn/${c.path || c.key}`}
                  className="group block rounded-2xl p-7 transition-transform hover:-translate-y-0.5"
                  style={{ background: '#FFFFFF', boxShadow: '0 4px 18px rgba(42,30,92,0.07)', borderTop: `4px solid ${GOLD}` }}
                  data-testid={`learn-bucket-${c.key}`}
                >
                  <h2 className="text-xl sm:text-2xl leading-snug mb-2.5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                    {c.name}
                  </h2>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: INK2 }}>{c.description}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: INK }}>
                    {c.count > 0
                      ? `${c.count} guide${c.count === 1 ? '' : 's'}`
                      : 'Guides for this topic are on the way'}
                    {c.count > 0 && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </Shell>
  );
}

const GuideRow = ({ a }) => (
  <Link
    to={`/learn/${a.slug}`}
    className="group block rounded-2xl p-6 transition-transform hover:-translate-y-0.5"
    style={{ background: '#FFFFFF', boxShadow: '0 4px 18px rgba(42,30,92,0.07)', borderLeft: `4px solid ${GOLD}` }}
    data-testid={`learn-card-${a.slug}`}
  >
    <h3 className="text-lg leading-snug mb-1.5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
      {a.title}
    </h3>
    {a.seo_description && (
      <p className="text-sm leading-relaxed mb-3" style={{ color: INK2 }}>{a.seo_description}</p>
    )}
    <span className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: INK }}>
      Read
      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
    </span>
  </Link>
);
